import { SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";
import { shouldIgnoreError } from "../config/signozConfig";
import { store } from "../config/store";

// Pengganti logging request/error bawaan @echoteam/signoz-react, yang mencatat
// body request/respons (password, data pribadi) dan URL halaman lengkap (rute
// reset password membawa token + email). Di sini hanya metadata yang dicatat.
// Library tetap dipakai untuk provider + exporter (lihat src/index.js).

const API_TRACER = "caready-web-api";
const ERROR_TRACER = "caready-web-error";
const REQUEST_ID_HEADER = "X-Request-ID";
// Endpoint bid: unit dan nominal dicatat (bukan data pribadi) untuk menelusuri bid.
const BID_PATH_PATTERN = /(send-bid|\/bid|\/time-bid|\/close-bid)$/;
// Header X-Request-ID baru boleh dikirim setelah CORS API mengizinkannya;
// tanpa itu preflight browser gagal dan semua request terblokir.
const SEND_REQUEST_ID = process.env.REACT_APP_SIGNOZ_SEND_REQUEST_ID === "true";
// Segmen path yang berisi email atau token panjang disamarkan.
const SENSITIVE_SEGMENT = /@|^[A-Za-z0-9._~-]{24,}$/;

// ID numerik user yang login (users.id), BUKAN email/nama: cukup untuk mencari
// semua span milik satu user (email -> users.id lewat DB), tanpa data pribadi.
const currentUserAttributes = () => {
  const userId = store.getState()?.authReducer?.user?.id;
  return userId ? { "enduser.id": String(userId) } : {};
};

// Format diterima backend: ^[A-Za-z0-9_-]{1,64}$
export const createRequestId = () => `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const decodeSegment = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch (e) {
    return segment;
  }
};

// Path tanpa query/hash dan tanpa segmen sensitif, mis.
// /reset-password/<token>/<email> -> /reset-password/:param/:param
export const sanitizePath = (url) => {
  const path = (url || "/").split(/[?#]/)[0];
  return path
    .split("/")
    .map((segment) => (SENSITIVE_SEGMENT.test(decodeSegment(segment)) ? ":param" : segment))
    .join("/");
};

const parseBody = (data) => {
  if (!data) return null;
  if (typeof data === "object") return data;
  try {
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
};

const isBidPath = (path) => BID_PATH_PATTERN.test(path);

const bidAttributes = (path, data) => {
  if (!isBidPath(path)) return {};
  const body = parseBody(data);
  if (!body) return {};
  const attributes = {};
  if (body.unit_id !== undefined) attributes["bid.unit_id"] = String(body.unit_id);
  if (body.offer !== undefined) attributes["bid.offer"] = Number(body.offer);
  return attributes;
};

// Pesan hasil bid dari server (mis. "Anda kalah cepat!"), bukan seluruh body.
const bidResultMessage = (data) => {
  const message = data?.errors?.details || data?.message;
  return typeof message === "string" ? message.slice(0, 200) : null;
};

const ensureRequestId = (config) => {
  const existing = config.headers?.get ? config.headers.get(REQUEST_ID_HEADER) : config.headers?.[REQUEST_ID_HEADER];
  if (existing) return existing;
  const requestId = createRequestId();
  if (!SEND_REQUEST_ID) return requestId;
  if (config.headers?.set) {
    config.headers.set(REQUEST_ID_HEADER, requestId);
  } else {
    config.headers = { ...(config.headers || {}), [REQUEST_ID_HEADER]: requestId };
  }
  return requestId;
};

const endSpan = (config, attributes, errorMessage, responseData) => {
  const tracking = config?.__apiTrace;
  if (!tracking) return;
  const { span, startedAt, isBid } = tracking;
  const resultMessage = isBid ? bidResultMessage(responseData) : null;
  span.setAttributes({
    ...attributes,
    ...(resultMessage ? { "bid.result_message": resultMessage } : {}),
    duration_ms: Date.now() - startedAt,
  });
  if (errorMessage) span.setStatus({ code: SpanStatusCode.ERROR, message: errorMessage });
  span.end();
};

/**
 * Pasang tracing pada instance axios. Harus dipanggil SEBELUM interceptor
 * respons lain didaftarkan: interceptor error di axiosConfig mengubah error
 * jaringan menjadi respons sukses (status 408) sehingga config tidak terbawa.
 */
export const setupApiTracing = (axiosInstance) => {
  if (!axiosInstance?.interceptors) return;

  axiosInstance.interceptors.request.use((config) => {
    const method = (config.method || "GET").toUpperCase();
    const path = sanitizePath(config.url);
    const requestId = ensureRequestId(config);
    const span = trace.getTracer(API_TRACER).startSpan(`HTTP ${method} ${path}`, {
      kind: SpanKind.CLIENT,
      attributes: {
        "http.method": method,
        "http.route": path,
        "http.request_id": requestId,
        "http.request_id_sent": SEND_REQUEST_ID,
        "page.route": sanitizePath(window.location.pathname),
        ...currentUserAttributes(),
        ...bidAttributes(path, config.data),
      },
    });
    config.__apiTrace = { span, startedAt: Date.now(), isBid: isBidPath(path) };
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      endSpan(response.config, { "http.status_code": response.status }, null, response.data);
      return response;
    },
    (error) => {
      const status = error?.response?.status;
      endSpan(
        error?.config,
        {
          ...(status ? { "http.status_code": status } : {}),
          "error.type": error?.code || error?.name || "Error",
        },
        error?.message,
        error?.response?.data
      );
      return Promise.reject(error);
    }
  );
};

const recordError = (type, error, message) => {
  if (shouldIgnoreError(error || message)) return;
  const span = trace.getTracer(ERROR_TRACER).startSpan(`Error: ${error?.name || type}`);
  span.setAttributes({
    "error.type": type,
    "error.name": error?.name || "Error",
    "error.message": String(error?.message || message || "").slice(0, 500),
    "page.route": sanitizePath(window.location.pathname),
    ...currentUserAttributes(),
  });
  if (error?.stack) span.setAttribute("error.stack", error.stack);
  span.setStatus({ code: SpanStatusCode.ERROR, message: error?.message || message });
  span.end();
};

/**
 * Error tracking tanpa URL lengkap (pengganti enableErrorTracking library).
 */
export const setupErrorTracking = () => {
  window.addEventListener("error", (event) => recordError("unhandled", event.error, event.message));
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    recordError("unhandled_rejection", reason instanceof Error ? reason : null, String(reason));
  });
};

// Jejak modal pindah lot di lelang live (sama dengan mobile, span dan atribut
// bernama sama): kapan modal hasil lot tampil dan bagaimana modal itu tertutup
// (otomatis setelah jeda atau ditutup user), supaya bid ke lot berikutnya bisa
// ditelusuri terhadap kondisi layar user. Hanya ID numerik, tanpa data pribadi.
const AUCTION_TRACER = "caready-web-auction";

export const NEXT_LOT_MODAL_TYPE = {
  WIN: "win",
  NOT_WIN: "not_win",
  UNIT_STATUS: "unit_status",
};

export const NEXT_LOT_MODAL_CLOSE_REASON = {
  // Jeda pindah lot habis, modal tertutup otomatis
  AUTO_TIMEOUT: "auto_timeout",
  // User menekan "Lanjut Lelang"
  USER_CONTINUE: "user_continue",
  // Pemenang menutup modal menang
  WINNER_CLOSE: "winner_close",
  // NextLotResponse baru datang sebelum lot sebelumnya diterapkan
  REPLACED: "replaced",
  // Halaman ditinggalkan sebelum lot diterapkan
  DISCARDED: "discarded",
};

const lotAttributes = ({ auctionId, modalType, closedUnitId, nextUnitId } = {}) => {
  const attributes = {};
  if (auctionId != null) attributes["auction.id"] = String(auctionId);
  if (modalType) attributes["modal.type"] = modalType;
  if (closedUnitId != null) attributes["lot.closed_unit_id"] = String(closedUnitId);
  if (nextUnitId != null) attributes["lot.next_unit_id"] = String(nextUnitId);
  return attributes;
};

const emitAuctionSpan = (name, attributes) => {
  trace
    .getTracer(AUCTION_TRACER)
    .startSpan(name, { attributes: { ...attributes, ...currentUserAttributes() } })
    .end();
};

export const traceNextLotModalShown = (lot) => {
  emitAuctionSpan("next_lot_modal_shown", lotAttributes(lot));
};

export const traceNextLotModalClosed = (lot, reason, visibleMs) => {
  const visible = Number.isFinite(visibleMs) ? { "modal.visible_ms": Math.max(0, Math.round(visibleMs)) } : {};
  emitAuctionSpan("next_lot_modal_closed", {
    ...lotAttributes(lot),
    "close.reason": reason,
    ...visible,
  });
};
